import React, { useRef, useContext, useState, useEffect } from "react";
import { FaPlus } from "react-icons/fa";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils";
import Notification from "../../components/UI/Notification";
import { Link } from "react-router-dom";
import Drawer from "../../components/UI/Drawer";
import DeleteButton from "../../components/UI/AlertDialog";
import ReactPaginate from 'react-paginate';
import { X } from "lucide-react";
import { AuthContext } from "../../contexts/AuthContext";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import DropdownMenu from "../../components/UI/DropdownMenu";
import { MoreVertical, AlertTriangle } from 'lucide-react';
import StatusBadge from '../../components/UI/StatusBadge';

const STORequests = () => {
    const [requests, setRequests] = useState([]);
    const { user } = useContext(AuthContext)
    const [formData, setFormData] = useState({
      request_date: "",
      minutes: 0,
      request_type: "",
      from_time: "",
      to_time: "",
      remark: "",
    });
    const [formErrors, setFormErrors] = useState({});
    const [filter, setFilter] = useState("All");
    const [editingId, setEditingId] = useState(null);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [notification, setNotification] = useState(null);
    const token = getToken()
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        perPage: 15,
      });
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);
    const [isFiltering, setIsFiltering] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [activeFilters, setActiveFilters] = useState({});
    const [filteredData, setFilteredData] = useState(requests);
    const [tempFilters, setTempFilters] = useState({});
    const { roles = [] } = useContext(AuthContext);
    const [openMenu, setOpenMenu] = useState(false);
    const buttonRef = useRef(null);

    const getRoleBasedRouteLocal = (route) => getRoleBasedRoute(route, roles);

    useEffect(() => {
      fetchRequests();
    }, [token]);

    const toggleFilterMenu = () => {
        setShowFilterMenu(!showFilterMenu);
    };

    const handleFilterChange = (field, value) => {
        setActiveFilters(prev => ({
        ...prev,
        [field]: value
        }));
    };

    const clearFilters = () => {
        setActiveFilters({});
        setSearchQuery("");
    };

    const clearFilterByKey = (key) => {
        const updatedFilters = { ...activeFilters };
        delete updatedFilters[key];
        setActiveFilters(updatedFilters);
    };

    // Define the filterable columns based on your table structure
    const filterableColumns = [
        { field: "request_date", label: "Request Date" },
        { field: "request_type", label: "Request Type" },
        { field: "status", label: "Status" },
        // { field: "reason", label: "Remark" },
    ];

    useEffect(() => {
    if (showFilterMenu) {
        setTempFilters(activeFilters);
    }
    }, [showFilterMenu]);

    useEffect(() => {
        setFilteredData(requests);
    }, [requests]);

    useEffect(() => {
        // Apply both search query and filters
        const normalizedRequests = requests.map(req => ({
            ...req,
            status: req.request?.status || ""
        }));

        let result = normalizedRequests;
        // let result = requests;

        // Apply search query filtering
        if (searchQuery) {
        const lowercasedQuery = searchQuery.toLowerCase();
        result = result.filter((item) => {
            // Search across all relevant fields
            return (
            (item.swipe_type?.toLowerCase().includes(lowercasedQuery)) ||
            (item.request_date?.toLowerCase().includes(lowercasedQuery)) ||
            (item.in_out_mode?.toString().toLowerCase().includes(lowercasedQuery)) ||
            (item.reason?.toLowerCase().includes(lowercasedQuery))
            (item.status?.toLowerCase().includes(lowercasedQuery))  
            );
        });
        }

        // Apply column-specific filters
        Object.entries(activeFilters).forEach(([field, filterValue]) => {
        if (filterValue) {
            result = result.filter(item => {
            const value = item[field];
            if (field === 'created_at' && value) {
                // Date filtering
                const itemDate = new Date(value);
                const day = String(itemDate.getDate()).padStart(2, '0');
                const month = String(itemDate.getMonth() + 1).padStart(2, '0');
                const year = itemDate.getFullYear();
                const formattedDate = `${day}-${month}-${year}`;
                
                return formattedDate.includes(filterValue);
            } else {
                // Standard string filtering
                return value?.toString().toLowerCase().includes(filterValue.toLowerCase());
            }
            });
        }
        });

        const filteringNow = searchQuery.length > 0 || Object.values(activeFilters).some(v => !!v);
        setIsFiltering(filteringNow);
        setFilteredData(result);
    }, [searchQuery, activeFilters, requests]);
        
    
    // fetch short time off requests
    const fetchRequests = async (page = 1) => {
      try {
        setLoading(true)
        const response = await axios.get(`https://hrmspayroll.prepstripe.com/api/sto-requests?page=${page}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        setLoading(false)
        setRequests(response.data.data);
        setPagination({
            currentPage: response.data?.current_page || 1,
            totalPages: response.data?.last_page || 1,
            totalItems: response.data?.total || 0,
            perPage: response.data?.per_page || 10,
        });
      } catch (error) {
        setLoading(false)
        console.error("Error fetching STO requests:", error);
      }
    };
  
    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    };
  
    useEffect(() => {
      if (formData.from_time && formData.to_time) {
        // Ensure time is in HH:MM:SS format
        const formatTime = (time) => {
          // If time is in HH:MM format, add :00 seconds
          if (time.includes(":") && time.length === 5) {
            return `${time}:00`;
          }
          return time;
        };
  
        const fromTime = formatTime(formData.from_time);
        const toTime = formatTime(formData.to_time);
  
        const fromTimeParts = fromTime.split(':').map(Number);
        const toTimeParts = toTime.split(':').map(Number);
  
        // Calculate total minutes
        const fromMinutes = fromTimeParts[0] * 60 + fromTimeParts[1];
        const toMinutes = toTimeParts[0] * 60 + toTimeParts[1];
        
        const diff = toMinutes - fromMinutes;
  
        setFormData((prevData) => ({
          ...prevData,
          minutes: diff > 0 ? diff : 0,
        }));
      }
    }, [formData.from_time, formData.to_time]);

    const handleSubmit = async (e) => {
        e.preventDefault();
    
        const submissionData = {
            ...formData,
            from_time: formData.from_time.length === 5 ? `${formData.from_time}:00` : formData.from_time,
            to_time: formData.to_time.length === 5 ? `${formData.to_time}:00` : formData.to_time,
        };
    
        if (editingId) {
            // Edit existing request
            try {
                await axios.put(`https://hrmspayroll.prepstripe.com/api/sto-requests/${editingId}`, submissionData, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });
                setRequests(prev =>
                    prev.map((req) =>
                        req.id === editingId ? { ...req, ...submissionData } : req
                    )
                );
                setNotification({
                    message: "Short-Time Off request updated successfully",
                    type: "success"
                })
                setEditingId(null);
                setIsModalOpen(false);
                setFormData({
                    request_date: "",
                    minutes: 0,
                    request_type: "",
                    from_time: "",
                    to_time: "",
                    remark: "",
                });
                setFormErrors({});
            } catch (error) {
                console.error("Error updating STO request:", error);
                if (error.response?.data?.errors) {
                    setFormErrors(error.response.data.errors);
                } else {
                    const errorMsg = error.response?.data?.error || error.message || "An unexpected error occurred while updating";
                    setNotification({
                        message: `Failed to update request: ${errorMsg}`,
                        type: "error"
                    })
                }
            }
        } else {
            // Create new request
            try {
                const response = await axios.post("https://hrmspayroll.prepstripe.com/api/sto-requests", submissionData, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });
    
                setRequests([...requests, response.data]);
                setNotification({
                    message: "Short-Time Off request added successfully",
                    type: "success"
                })
                fetchRequests();
                setIsModalOpen(false);
                // Reset form on success
                setFormData({
                    request_date: "",
                    minutes: 0,
                    request_type: "",
                    from_time: "",
                    to_time: "",
                    remark: "",
                });
                setFormErrors({});
            } catch (error) {
                console.error("Error creating STO request:", error);
                if (error.response?.data?.errors) {
                    setFormErrors(error.response.data.errors);
                } else {
                    const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || "An unexpected error occurred while creating";
                    setNotification({
                        message: `Failed to create request: ${errorMsg}`,
                        type: "error"
                    })
                }
            }
        }
    };
    
    // edit short time off
    const handleEdit = (request) => {
        setFormData({
            request_date: request.request_date,
            minutes: request.minutes,
            request_type: request.request_type,
            from_time: request.from_time,
            to_time: request.to_time,
            remark: request.remark,
        });
        setEditingId(request.id);
        setIsModalOpen(true);
    };

    // delete short time off
    const handleDelete = async (id) => {
        try {
        await axios.delete(`https://hrmspayroll.prepstripe.com/api/sto-requests/${id}`, {
            headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            },
        });
        setRequests(requests.filter((request) => request.id !== id));
        } catch (error) {

        console.error("Error deleting STO request:", error);
            // setNotification(error.response?.data?.error || "An error occurred while deleting the request");
            setNotification({
                message: `${error.response?.data?.error || "An error occurred while deleting the request"}`,
                type: "error"
            })
        }
    };

    const handlePageChange = (newPage) => {
        // console.log("current page:", newPage)
        if (newPage >= 1 && newPage <= pagination.totalPages) {
          setCurrentPage(newPage);
          fetchRequests(newPage);
        }
    };
    
    const renderPageNumbers = () => {
        const pages = [];
        for (let i = 1; i <= pagination.totalPages; i++) {
        pages.push(
            <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={`px-4 py-2 rounded-md mx-1 ${
                currentPage === i ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
            >
            {i}
            </button>
        );
        }
        return pages;
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        const filtering = value !== '';
        setIsFiltering(filtering);
      
        if (!filtering) {
          // Search cleared, restore current page's data
          handlePageChange(currentPage); // fetchResponses will be called inside
        }
    };
      

    const filteredRequests = filter === "All" 
    ? requests 
    : requests.filter(req => req.request.status.toLowerCase() === filter.toLowerCase());
    
    // Further filter by search term
    const searchedLeaves = filteredRequests.filter((leave) =>
        leave.request_type.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const hasTempFilters = Object.values(tempFilters).some((val) => val !== "");

  return (
    <div className="px-4 py-2 bg-white dark:bg-gray-900 rounded-lg">
        {/* notification compo */}
        {notification && (
            <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
        )}
        {/* notification compo */}
     
        {/* Breadcrumb */}
        <nav className="flex flex-wrap mb-4 font-poppins" aria-label="Breadcrumb">
            <ol class="inline-flex flex-wrap items-center space-x-1 md:space-x-2">
            <li>
                <div className="flex items-center">
                    <Link
                    to={getRoleBasedRouteLocal("/admin/attendance_management")}
                    className="text-sm sm:text-md font-medium text-black hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-500"
                    >
                    Attendance Management
                    </Link>
                </div>
            </li>
            <li aria-current="page">
                <div class="flex items-center">
                <svg class="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 9 4-4-4-4"/>
                </svg>
                <span class="ms-1 text-sm sm:text-md font-medium text-gray-500 md:ms-2 dark:text-gray-400">Short Time-Off Request</span>
                </div>
            </li>
            </ol>
        </nav>

        <h2 className="text-lg sm:text-xl dark:text-white font-poppins font-semibold mb-4">Short Time-Off Requests</h2>

        {/* <button onClick={() => setIsModalOpen(true)} className="bg-blue-500 text-white px-4 py-2 mb-4">New Request</button>
         */}

        {/* create/filter button */}
        <div className="flex flex-col sm:flex-row gap-2 justify-between items-center mb-4 font-poppins">
            {/* search field */}
            <div className="relative w-full sm:w-96">
                <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full dark:bg-black dark:text-white dark:border-gray-700 placeholder:text-xs md:placeholder:text-sm p-2 pr-8 border border-gray-300 rounded-md"
                />
                {searchQuery && (
                    <button
                        onClick={() => setSearchQuery("")}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Clear search"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>
            {/* Add button at right end */}
            <button
                onClick={() => {
                    if (!user.manager) {
                        setNotification({
                            message: "Manager is not allocated.",
                            type: "error",
                        });
                        return;
                    }
                    setIsModalOpen(true);
                }}
                className="flex items-center justify-center gap-2 bg-blue-600 font-medium hover:bg-blue-700 text-white px-4 py-2 rounded-md whitespace-nowrap">
                <FaPlus /> Apply Short-Time Request
            </button>
        </div>
      

        {/* table data */}
        <div className="overflow-x-auto bg-white dark:bg-gray-900 font-poppins shadow-md rounded-lg">
            <table className="min-w-full table-auto md:table-fixed text-sm">
                <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
                    <tr>
                        <th className="py-2 px-4 dark:text-white font-semibold text-center text-xs md:text-sm text-black whitespace-nowrap">Sr No.</th>
                        <th className="py-2 px-4 dark:text-white font-semibold text-center text-xs md:text-sm text-black whitespace-nowrap">Date</th>
                        <th className="py-2 px-4 dark:text-white font-semibold text-center text-xs md:text-sm text-black whitespace-nowrap">Request Type</th>
                        <th className="py-2 px-4 dark:text-white font-semibold text-center text-xs md:text-sm text-black whitespace-nowrap">Status</th>
                        <th className="py-2 px-4 dark:text-white font-semibold text-center text-xs md:text-sm text-black whitespace-nowrap">Actions</th>
                    </tr>
                </thead>
                <tbody>
                {loading ? (
                    <tr className="border-b hover:bg-gray-100 dark:hover:bg-gray-800">
                        <td colSpan="5" className="py-2 px-4 text-xs md:text-sm whitespace-nowrap">
                            <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2 mt-2"></div>
                        </td>
                    </tr>
                ) : (
                    filteredData.length > 0 ? (
                        filteredData.map((request, index) => (
                            <tr key={request.id} className="dark:border-gray-700 dark:hover:bg-gray-950 dark:hover:text-white dark:text-white">
                                <td className="py-2 px-4 dark:text-white text-center whitespace-nowrap font-medium">{index + 1}</td>
                                <td className="p-2 dark:text-white text-center whitespace-nowrap">{request.request_date}</td>
                                <td title={request.request_type} className="p-2 dark:text-white text-center truncate whitespace-nowrap">{request.request_type}</td>
                                <td className="p-2 dark:text-white text-center truncate whitespace-nowrap">
                                    <StatusBadge status={request?.request?.status} />
                                </td>

                                <td className="p-2 flex justify-center gap-2 whitespace-nowrap">
   
                                    <button
                                        ref={buttonRef}
                                        className=""
                                        onClick={() => setOpenMenu((prev) => !prev)}
                                        >
                                        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />   
                                    </button>

                                    {openMenu && (
                                        <DropdownMenu
                                            buttonRef={buttonRef}
                                            onClose={() => setOpenMenu(false)}
                                        >
                                            <button
                                            className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                                            onClick={() => {
                                                setSelectedRequest(request);
                                                setDrawerOpen(true);
                                                setOpenMenu(false);
                                            }}
                                            >
                                            View
                                            </button>
                                            {request.request.status === "pending" && (
                                            <button
                                                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                                                onClick={() => {
                                                handleEdit(request);
                                                setOpenMenu(false);
                                                }}
                                            >
                                                Edit
                                            </button>
                                            )}
                                            <DeleteButton
                                            style={
                                                "block w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                                            }
                                            onDelete={() => {
                                                handleDelete(request.id);
                                                setOpenMenu(false);
                                            }}
                                            />
                                        </DropdownMenu>
                                    )}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={5} className="text-center text-gray-500 p-2">No record found</td>
                        </tr>
                    )
                )}
                </tbody>
            </table>
        </div>

        {/* pagination */}
        {!isFiltering && filteredData?.length > 0 && (
            <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={currentPage - 1}
            pageRangeDisplayed={5}
            disabledClassName='opacity-50 cursor-not-allowed'
            disabledLinkClassName='pointer-events-none'
            pageCount={pagination.totalPages}
            previousLabel="< Prev"
            previousClassName='dark:text-white border border-gray-300 rounded-md dark:border-gray-600'
            previousLinkClassName='block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
            nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            renderOnZeroPageCount={null}
            containerClassName="flex items-center justify-center mt-4 font-poppins"
            pageClassName="mx-1 border rounded dark:text-white font-poppins"
            pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
            activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
          />
        )}

        {/* create/edit short time off requests */}
        {isModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end md:items-center z-[1001] backdrop-blur-sm" onClick={() => { setIsModalOpen(false); setEditingId(null); setFormData({ request_date: "", minutes: 0, request_type: "", from_time: "", to_time: "", remark: "" }); }}>
                <div className="bg-white font-poppins dark:bg-gray-900 rounded-md shadow-xl w-full max-w-md mx-auto relative transition-transform duration-300 transform md:animate-none animate-slideUp" onClick={(e) => e.stopPropagation()}>
                    {/* <h3 className="text-lg font-semibold mb-4">{editingId ? "Edit" : "New"} Short Time-Off Request</h3> */}
                    <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-900 sm:rounded-lg rounded-t-2xl">
                        
                        {/* modal title */}
                        <div className="relative">
                            <div className="absolute inset-0  dark:from-gray-800 dark:to-gray-850"></div>
                            <div className="relative flex items-center justify-between p-3 ">
                                <div className="flex items-center space-x-3">
                                    {/* <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div> */}
                                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
                                       {editingId ? "Edit" : "Short-Time Request"}
                                    </h2>
                                </div>
                                
                                {/* Enhanced close button */}
                                <button
                                    type="button"
                                    onClick={() => { 
                                        setIsModalOpen(false); 
                                        setEditingId(null); 
                                        setFormData({ 
                                            request_date: "", 
                                            minutes: 0, 
                                            request_type: "", 
                                            from_time: "", 
                                            to_time: "", 
                                            remark: "" 
                                        }); 
                                    }} 
                                    className="group relative p-2 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
                                    aria-label="Close modal">
                                    <X 
                                    size={20} 
                                    className="text-gray-600 transition-colors duration-200" 
                                    />
                                    <div className="absolute inset-0 rounded-xl transition-all duration-200"></div>
                                </button>
                            </div>
                        </div>

                        {/* modal body */}
                        <div className="max-h-[80vh] overflow-y-auto flex flex-col gap-y-2 bg-white dark:bg-gray-900 p-4">
                            {/* date field */}
                            {/* <label className="md:text-md text-sm dark:text-white">
                                Date: <span className="text-red-500 ml-1">*</span>
                            
                                <input
                                    type="date"
                                    value={formData.request_date}
                                    onChange={(e) => setFormData({ ...formData, request_date: e.target.value })}
                                    min={new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0]}
                                    max={new Date().toISOString().split("T")[0]} // Today's date
                                    required
                                    className="w-full dark:bg-black dark:text-white dark:focus:outline-none dark:border-gray-700 p-2 border rounded mb-2"
                                />
                            </label> */}

                             <label className="md:text-md text-sm dark:text-white">
                             Date: <span className="text-red-500 ml-1">*</span>
                                 <input
                                     type="date"
                                     value={formData.request_date}
                                     onChange={(e) => {
                                         setFormData({ ...formData, request_date: e.target.value });
                                         if (formErrors.request_date) setFormErrors({ ...formErrors, request_date: "" });
                                     }}
                                     min={new Date().toISOString().split("T")[0]} // Today's date as minimum
                                     required
                                     className={`w-full dark:bg-black dark:text-white dark:focus:outline-none dark:border-gray-700 p-2 border rounded mb-2 ${
                                         formErrors.request_date ? 'border-red-500 focus:border-red-500' : ''
                                     }`}
                                 />
                                 {formErrors.request_date && (
                                     <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                                         <AlertTriangle className="w-4 h-4" />
                                         {formErrors.request_date}
                                     </p>
                                 )}
                             </label>


                             {/* minutes and request type field */}
                            <div className="grid grid-cols-2 gap-4 md:text-md text-sm">
                                <div>
                                    <label className="dark:text-white">Minutes: <span className="text-red-500 ml-1">*</span></label>
                                    <input 
                                        disabled 
                                        name="minutes" 
                                        value={formData.minutes} 
                                        readOnly 
                                        className={`w-full dark:bg-black dark:text-white dark:focus:outline-none dark:border-gray-700 p-2 border rounded ${
                                            formErrors.minutes ? 'border-red-500 focus:border-red-500' : ''
                                        }`} 
                                    />
                                    {formErrors.minutes && (
                                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1 font-poppins">
                                            <AlertTriangle className="w-3.5 h-3.5" />
                                            {formErrors.minutes}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="dark:text-white">Request Type: <span className="text-red-500 ml-1">*</span></label>
                                    <select 
                                        name="request_type" 
                                        value={formData.request_type} 
                                        onChange={(e) => {
                                            handleChange(e);
                                            if (formErrors.request_type) setFormErrors({ ...formErrors, request_type: "" });
                                        }} 
                                        required 
                                        className={`w-full dark:bg-black dark:text-white dark:focus:outline-none dark:border-gray-700 p-2 border rounded ${
                                            formErrors.request_type ? 'border-red-500 focus:border-red-500' : ''
                                        }`}
                                    >
                                        <option value="">Select Option</option>
                                        <option value="Personal Errand">Personal Errand</option>
                                        <option value="Medical Appointment">Medical Appointment</option>
                                        <option value="Family Emergency">Family Emergency</option>
                                        <option value="Official Work">Official Work</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    {formErrors.request_type && (
                                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1 font-poppins">
                                            <AlertTriangle className="w-3.5 h-3.5" />
                                            {formErrors.request_type}
                                        </p>
                                    )}
                                </div>
                            </div>

 
                             {/* from to time field */}
                            <div className="grid grid-cols-2 gap-4 md:text-md text-sm">
                                <div>
                                    <label className="dark:text-white">
                                    From: <span className="text-xs">(Time)</span> <span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <input
                                    type="time"
                                    placeholder="From Time"
                                    value={formData.from_time}
                                    onChange={(e) => {
                                        const newFromTime = e.target.value;
                                        // Optional: Reset to_time if it becomes invalid
                                        const isToValid = !formData.to_time || newFromTime < formData.to_time;
                                        setFormData({
                                        ...formData,
                                        from_time: newFromTime,
                                        ...(isToValid ? {} : { to_time: "" }),
                                        });
                                        if (formErrors.from_time) setFormErrors({ ...formErrors, from_time: "" });
                                    }}
                                    required
                                    className={`w-full dark:bg-black dark:text-white dark:focus:outline-none dark:border-gray-700 p-2 border rounded ${
                                        formErrors.from_time ? 'border-red-500 focus:border-red-500' : ''
                                    }`}
                                    />
                                    {formErrors.from_time && (
                                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1 font-poppins">
                                            <AlertTriangle className="w-3.5 h-3.5" />
                                            {formErrors.from_time}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="dark:text-white">
                                    To: <span className="text-xs">(Time)</span> <span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <input
                                    type="time"
                                    placeholder="To Time"
                                    value={formData.to_time}
                                    onChange={(e) => {
                                        const newToTime = e.target.value;
                                        if (newToTime <= formData.from_time) {
                                        alert("To time must be greater than From time.");
                                        } else {
                                        setFormData({ ...formData, to_time: newToTime });
                                        }
                                        if (formErrors.to_time) setFormErrors({ ...formErrors, to_time: "" });
                                    }}
                                    required
                                    className={`w-full dark:bg-black dark:text-white dark:focus:outline-none dark:border-gray-700 p-2 border rounded ${
                                        formErrors.to_time ? 'border-red-500 focus:border-red-500' : ''
                                    }`}
                                    />
                                    {formErrors.to_time && (
                                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1 font-poppins">
                                            <AlertTriangle className="w-3.5 h-3.5" />
                                            {formErrors.to_time}
                                        </p>
                                    )}
                                </div>
                            </div>
                            
                             {/* remark field */}
                            <label className="md:text-md text-sm dark:text-white">
                                Remark: <span className="text-red-500 ml-1">*</span>
                                <textarea 
                                    placeholder="Remark" 
                                    value={formData.remark} 
                                    // onChange={(e) => setFormData({ ...formData, remark: e.target.value })} 
                                    required 
                                    onChange={(e) => {
                                        const input = e.target.value;
                                        const filteredInput = input.replace(/[^a-zA-Z\s.,!?']/g, '');
                                        setFormData({ ...formData, remark: filteredInput });
                                        if (formErrors.remark) setFormErrors({ ...formErrors, remark: "" });
                                    }}
                                    maxLength={50}
                                    className={`w-full dark:bg-black dark:text-white dark:focus:outline-none dark:border-gray-700 p-2 border rounded ${
                                        formErrors.remark ? 'border-red-500 focus:border-red-500' : ''
                                    }`}>
                                </textarea>
                                {formErrors.remark && (
                                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1 font-poppins">
                                        <AlertTriangle className="w-3.5 h-3.5" />
                                        {formErrors.remark}
                                    </p>
                                )}
                                <p className="text-sm text-gray-500">{formData.remark.length}/50 characters</p>
                            </label>
                        </div>
                        
                        {/* modal footer */}
                        <div className="px-4 py-5 flex justify-end gap-2 items-center dark:bg-gray-900">
                            <button type="button" onClick={() => { setIsModalOpen(false); setEditingId(null); setFormData({ request_date: "", minutes: 0, request_type: "", from_time: "", to_time: "", remark: "" }); }} className="px-7 py-2 text-black hover:bg-gray-200 font-medium dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md">Cancel</button>
                            <button type="submit" className="bg-blue-600 hover:bg-blue-700 font-medium rounded text-white px-10 py-2">{editingId ? "Update" : "Save"}</button>
                        </div>
                    </form>
                </div>
            </div>
        )}

        {/* Modal to view details */}
        {drawerOpen && selectedRequest && (
            <div 
                className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[1001] backdrop-blur-sm p-4 font-poppins"
                onClick={() => setDrawerOpen(false)}
            >
                <div 
                    className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header with Gradient */}
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-2xl font-bold text-white uppercase tracking-tight">
                                    Short Time Off Details
                                </h3>
                            </div>
                            <button
                                onClick={() => setDrawerOpen(false)}
                                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                            >
                                <X className="w-6 h-6 text-white" />
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)] bg-white dark:bg-gray-900">
                        {/* Status Badge - Centered */}
                        <div className="mb-6 flex justify-center">
                            <StatusBadge status={selectedRequest.request?.status || 'pending'} />
                        </div>

                        {/* Information Cards Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            {/* Request Date */}
                            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Request Date</p>
                                        <p className="text-base font-semibold text-gray-900 dark:text-white">{selectedRequest.request_date}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Request Type */}
                            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                        <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Request Type</p>
                                        <p className="text-base font-semibold text-gray-900 dark:text-white">{selectedRequest.request_type}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Minutes */}
                            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                                        <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Minutes</p>
                                        <p className="text-base font-semibold text-gray-900 dark:text-white">{selectedRequest.minutes} Mins</p>
                                    </div>
                                </div>
                            </div>

                            {/* Duration */}
                            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                                        <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Duration</p>
                                        <p className="text-base font-semibold text-gray-900 dark:text-white">
                                            {selectedRequest.from_time} - {selectedRequest.to_time}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Remark Section */}
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                            <h4 className="text-base font-bold text-gray-800 dark:text-white mb-3 tracking-wide uppercase text-xs">Remark</h4>
                            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed font-medium">
                                {selectedRequest.remark || 'No remark provided'}
                            </p>
                        </div>

                        {/* Approver Comments Section */}
                        {selectedRequest.request?.approver_comments && selectedRequest.request?.action_date && (
                            <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl p-5 border border-blue-200 dark:border-blue-800 shadow-sm">
                                <h4 className="text-sm font-bold text-blue-600 dark:text-blue-400 mb-3 uppercase tracking-wider">
                                    Approver's Comment
                                </h4>
                                <p className="text-base text-gray-800 dark:text-gray-200 mb-3 font-semibold">
                                    {selectedRequest.request.approver_comments}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span>Request {selectedRequest.request.status} on: {selectedRequest.request.action_date}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t dark:border-gray-800 flex justify-end">
                        <button 
                            onClick={() => setDrawerOpen(false)}
                            className="px-8 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-bold hover:bg-gray-100 dark:hover:bg-gray-800 transition-all active:scale-95"
                        >
                            CLOSE
                        </button>
                    </div>
                </div>
            </div>
        )}
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div className="flex flex-col font-poppins">
    <span className="text-gray-500 dark:text-white">{label}</span>
    <span className="font-medium text-gray-800 dark:text-gray-500 break-words">{value || "-"}</span>
  </div>
);


export default STORequests;
